import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Button,
  Switch,
  TextField,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Autocomplete,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Send as SendIcon, Refresh as RefreshIcon, LockReset as LockResetIcon } from '@mui/icons-material';
import { Layout } from '../components/Layout';
import { QuotasContent, ActivityContent } from './QuotasPage';
import {
  useAdminUsers,
  useAdminUser,
  useAdminUpdatePermissions,
  useAdminUpdatePermissionsMutation,
  useAdminUpdateEnabled,
  useAdminUpdateEnabledMutation,
  useAdminInvite,
  useAdminResendInvite,
  useAdminRegenerateKey,
  useAdminResetTerms,
  useAdminDeleteUserMutation,
  useAdminApis,
  useAdminCreateApi,
  useAdminUpdateApi,
  useAdminDeleteApi,
} from '../hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import type { AllowedRule, ApiRegistration, AdminListApisResponse } from '../types';

export function AdminPage() {
  const [inviteEmail, setInviteEmail] = useState('');
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [permissionsEdit, setPermissionsEdit] = useState<AllowedRule[]>([]);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [regenerateFeedback, setRegenerateFeedback] = useState<
    { severity: 'success' | 'error'; message: string } | null
  >(null);
  const [resetTermsConfirmOpen, setResetTermsConfirmOpen] = useState(false);
  const [resetTermsFeedback, setResetTermsFeedback] = useState<
    { severity: 'success' | 'error'; message: string } | null
  >(null);
  const [userSearch, setUserSearch] = useState('');
  const [userSortBy, setUserSortBy] = useState<'email' | 'userId' | 'enabled' | 'permissions' | 'termsAcceptedAt'>('email');
  const [userSortDir, setUserSortDir] = useState<'asc' | 'desc'>('asc');

  const { data: usersData, isLoading: usersLoading, error: usersError } = useAdminUsers();
  const { data: userDetail, isLoading: userDetailLoading } = useAdminUser(selectedUserId);
  const { data: apiCatalog } = useAdminApis();
  const updatePermissions = useAdminUpdatePermissions(selectedUserId || '');
  const updateEnabled = useAdminUpdateEnabled(selectedUserId || '');
  const updateEnabledMutation = useAdminUpdateEnabledMutation();
  const invite = useAdminInvite();
  const resendInvite = useAdminResendInvite();
  const [resendFeedback, setResendFeedback] = useState<
    { severity: 'success' | 'error'; message: string } | null
  >(null);
  const deleteUser = useAdminDeleteUserMutation();
  const regenerateKey = useAdminRegenerateKey(selectedUserId || '');
  const resetTerms = useAdminResetTerms(selectedUserId || '');

  const handleResendInvite = async (userId: string, email?: string) => {
    setResendFeedback(null);
    try {
      await resendInvite.mutateAsync(userId);
      setResendFeedback({
        severity: 'success',
        message: `Invitation resent${email ? ` to ${email}` : ''}`,
      });
    } catch (err) {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      const serverMsg = anyErr.response?.data?.message || anyErr.message || 'unknown error';
      setResendFeedback({
        severity: 'error',
        message: `Cannot resend invitation: ${serverMsg}`,
      });
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await invite.mutateAsync(inviteEmail.trim());
      setInviteEmail('');
    } catch {
      // Error shown via invite.error
    }
  };

  const openUserDetail = (userId: string) => setSelectedUserId(userId);
  const closeUserDetail = () => {
    setSelectedUserId(null);
    setPermissionsDialogOpen(false);
    setRegenerateFeedback(null);
    setResetTermsFeedback(null);
  };

  const openPermissionsDialog = () => {
    setPermissionsEdit(userDetail ? [...userDetail.allowed] : []);
    setPermissionsDialogOpen(true);
  };

  const savePermissions = async () => {
    if (!selectedUserId) return;
    try {
      await updatePermissions.mutateAsync(permissionsEdit);
      setPermissionsDialogOpen(false);
    } catch {
      // Error shown via updatePermissions.error
    }
  };

  const handleEnabledChange = (userId: string, enabled: boolean) => {
    updateEnabledMutation.mutate({ userId, enabled });
  };

  const handleDeleteUserConfirm = async () => {
    if (!selectedUserId) return;
    try {
      await deleteUser.mutateAsync(selectedUserId);
      setDeleteConfirmOpen(false);
      closeUserDetail();
    } catch {
      // Error shown via deleteUser.error
    }
  };

  const handleRegenerateKeyConfirm = async () => {
    if (!selectedUserId) return;
    try {
      await regenerateKey.mutateAsync();
      setRegenerateConfirmOpen(false);
      setRegenerateFeedback({ severity: 'success', message: 'API key regenerated' });
    } catch {
      // Error surfaced via regenerateKey.isError inside the dialog
    }
  };

  const handleResetTermsConfirm = async () => {
    if (!selectedUserId) return;
    try {
      await resetTerms.mutateAsync();
      setResetTermsConfirmOpen(false);
      setResetTermsFeedback({ severity: 'success', message: 'Terms acceptance reset' });
    } catch {
      // Error surfaced via resetTerms.isError inside the dialog
    }
  };

  const location = useLocation();
  const navigate = useNavigate();
  const currentTab = location.pathname === '/admin/quotas' ? 1 : location.pathname === '/admin/activity' ? 2 : location.pathname === '/admin/apis' ? 3 : 0;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const paths = ['/admin', '/admin/quotas', '/admin/activity', '/admin/apis'];
    navigate(paths[newValue]);
  };

  const handleUserSort = (column: typeof userSortBy) => {
    if (userSortBy === column) {
      setUserSortDir(userSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortBy(column);
      setUserSortDir('asc');
    }
  };

  const filteredSortedUsers = (() => {
    if (!usersData) return [];
    const q = userSearch.trim().toLowerCase();
    const filtered = q
      ? usersData.users.filter((u) =>
          (u.email ?? '').toLowerCase().includes(q) ||
          u.userId.toLowerCase().includes(q) ||
          u.allowed.some((r) => r.prefix.toLowerCase().includes(q))
        )
      : usersData.users;
    const dir = userSortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (userSortBy) {
        case 'email':
          return (a.email ?? '').localeCompare(b.email ?? '') * dir;
        case 'userId':
          return a.userId.localeCompare(b.userId) * dir;
        case 'enabled':
          return ((a.enabled === b.enabled) ? 0 : a.enabled ? -1 : 1) * dir;
        case 'permissions':
          return (a.allowed.length - b.allowed.length) * dir;
        case 'termsAcceptedAt': {
          const av = a.termsAcceptedAt ?? '';
          const bv = b.termsAcceptedAt ?? '';
          if (av === bv) return 0;
          if (av === '') return 1;
          if (bv === '') return -1;
          return av.localeCompare(bv) * dir;
        }
        default:
          return 0;
      }
    });
  })();

  return (
    <Layout>
      <Typography variant="h4" gutterBottom>
        Admin
      </Typography>

      <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Users" />
        <Tab label="Quotas" />
        <Tab label="Activity" />
        <Tab label="APIs" />
      </Tabs>

      {currentTab === 3 && <ApisContent />}

      {currentTab === 2 && <ActivityContent />}

      {currentTab === 1 && <QuotasContent />}

      {currentTab === 0 && (<>
      {/* Invite */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Invite user
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              label="Email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
              sx={{ minWidth: 280 }}
            />
            <Button
              variant="contained"
              onClick={handleInvite}
              disabled={!inviteEmail.trim() || invite.isPending}
              startIcon={invite.isPending ? <CircularProgress size={16} /> : <AddIcon />}
            >
              Send invitation
            </Button>
            <Button
              variant="outlined"
              onClick={() => setBulkInviteOpen(true)}
              startIcon={<AddIcon />}
            >
              Bulk invite
            </Button>
          </Stack>
          {invite.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(invite.error)}
            </Alert>
          )}
          {invite.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Invitation sent. The user will receive an email with a temporary password.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Users list */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Users
            </Typography>
            <TextField
              size="small"
              placeholder="Search email, ID, prefix..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              sx={{ minWidth: 280 }}
            />
          </Box>
          {usersLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {usersError && (
            <Alert severity="error">Failed to load users.</Alert>
          )}
          {resendFeedback && (
            <Alert
              severity={resendFeedback.severity}
              onClose={() => setResendFeedback(null)}
              sx={{ mb: 2 }}
            >
              {resendFeedback.message}
            </Alert>
          )}
          {usersData && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={userSortBy === 'email' ? userSortDir : false}>
                      <TableSortLabel
                        active={userSortBy === 'email'}
                        direction={userSortBy === 'email' ? userSortDir : 'asc'}
                        onClick={() => handleUserSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={userSortBy === 'userId' ? userSortDir : false}>
                      <TableSortLabel
                        active={userSortBy === 'userId'}
                        direction={userSortBy === 'userId' ? userSortDir : 'asc'}
                        onClick={() => handleUserSort('userId')}
                      >
                        User ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={userSortBy === 'enabled' ? userSortDir : false}>
                      <TableSortLabel
                        active={userSortBy === 'enabled'}
                        direction={userSortBy === 'enabled' ? userSortDir : 'asc'}
                        onClick={() => handleUserSort('enabled')}
                      >
                        Enabled
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={userSortBy === 'termsAcceptedAt' ? userSortDir : false}>
                      <TableSortLabel
                        active={userSortBy === 'termsAcceptedAt'}
                        direction={userSortBy === 'termsAcceptedAt' ? userSortDir : 'asc'}
                        onClick={() => handleUserSort('termsAcceptedAt')}
                      >
                        Terms accepted at
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={userSortBy === 'permissions' ? userSortDir : false}>
                      <TableSortLabel
                        active={userSortBy === 'permissions'}
                        direction={userSortBy === 'permissions' ? userSortDir : 'asc'}
                        onClick={() => handleUserSort('permissions')}
                      >
                        Permissions
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSortedUsers.map((u) => (
                    <TableRow key={u.userId}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {u.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {u.userId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={u.enabled}
                          onChange={(e) => handleEnabledChange(u.userId, e.target.checked)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {u.termsAcceptedAt ? (
                          <Typography variant="body2">
                            {new Date(u.termsAcceptedAt).toLocaleString()}
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              Not accepted
                            </Typography>
                            <IconButton
                              size="small"
                              title="Resend invitation"
                              disabled={resendInvite.isPending}
                              onClick={() => handleResendInvite(u.userId, u.email)}
                            >
                              {resendInvite.isPending && resendInvite.variables === u.userId ? (
                                <CircularProgress size={16} />
                              ) : (
                                <SendIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.allowed.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {u.allowed.slice(0, 3).map((r, i) => (
                              <Chip key={i} label={`${r.prefix}`} size="small" sx={{ mb: 0.5 }} />
                            ))}
                            {u.allowed.length > 3 && (
                              <Chip label={`+${u.allowed.length - 3}`} size="small" />
                            )}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openUserDetail(u.userId)}>
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* User detail dialog */}
      <Dialog open={!!selectedUserId} onClose={closeUserDetail} maxWidth="sm" fullWidth>
        <DialogTitle>
          {userDetail?.email || 'User'}
          {userDetail && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              User ID: {selectedUserId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {userDetailLoading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress />
            </Box>
          )}
          {userDetail && !userDetailLoading && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {regenerateFeedback && (
                <Alert
                  severity={regenerateFeedback.severity}
                  onClose={() => setRegenerateFeedback(null)}
                >
                  {regenerateFeedback.message}
                </Alert>
              )}
              {resetTermsFeedback && (
                <Alert
                  severity={resetTermsFeedback.severity}
                  onClose={() => setResetTermsFeedback(null)}
                >
                  {resetTermsFeedback.message}
                </Alert>
              )}
              <Box display="flex" alignItems="center">
                <Typography variant="body2" sx={{ mr: 2 }}>
                  Enabled
                </Typography>
                <Switch
                  checked={userDetail.enabled}
                  onChange={(e) =>
                    updateEnabled.mutateAsync(e.target.checked)
                  }
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Allowed (prefix + methods)
                </Typography>
                <Button size="small" onClick={openPermissionsDialog}>
                  Edit permissions
                </Button>
                {userDetail.allowed.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No permissions
                  </Typography>
                ) : (
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {userDetail.allowed.map((r, i) => (
                      <Typography key={i} variant="body2" fontFamily="monospace">
                        {r.prefix} — {r.methods?.join(', ') || '—'}
                        {r.quotas != null ? ` — Quota: ${r.quotas}` : ' — No limit'}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={deleteUser.isPending}
          >
            Delete user
          </Button>
          <Button
            color="warning"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setRegenerateConfirmOpen(true)}
            disabled={regenerateKey.isPending}
          >
            Regenerate API key
          </Button>
          <Button
            color="warning"
            variant="outlined"
            startIcon={<LockResetIcon />}
            onClick={() => setResetTermsConfirmOpen(true)}
            disabled={resetTerms.isPending || !userDetail?.termsAcceptedAt}
          >
            Reset terms acceptance
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeUserDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete user confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => !deleteUser.isPending && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this user? This will remove their API key and their account.
          </Typography>
          {deleteUser.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(deleteUser.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteUser.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteUserConfirm}
            disabled={!selectedUserId || deleteUser.isPending}
            startIcon={deleteUser.isPending ? <CircularProgress size={16} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate API key confirmation dialog */}
      <Dialog
        open={regenerateConfirmOpen}
        onClose={() => !regenerateKey.isPending && setRegenerateConfirmOpen(false)}
      >
        <DialogTitle>Regenerate this user's API key?</DialogTitle>
        <DialogContent>
          <Typography>
            This will invalidate their current API key immediately. They will need to update any
            applications using the old key with the new one, which they can retrieve from their own
            dashboard. Continue?
          </Typography>
          {regenerateKey.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(regenerateKey.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRegenerateConfirmOpen(false)}
            disabled={regenerateKey.isPending}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleRegenerateKeyConfirm}
            disabled={!selectedUserId || regenerateKey.isPending}
            startIcon={regenerateKey.isPending ? <CircularProgress size={16} /> : <RefreshIcon />}
          >
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset terms acceptance confirmation dialog */}
      <Dialog
        open={resetTermsConfirmOpen}
        onClose={() => !resetTerms.isPending && setResetTermsConfirmOpen(false)}
      >
        <DialogTitle>Reset this user's terms acceptance?</DialogTitle>
        <DialogContent>
          <Typography>
            The user will be signed out immediately and must re-accept the terms of use on their
            next login. Continue?
          </Typography>
          {resetTerms.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(resetTerms.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResetTermsConfirmOpen(false)}
            disabled={resetTerms.isPending}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleResetTermsConfirm}
            disabled={!selectedUserId || resetTerms.isPending}
            startIcon={resetTerms.isPending ? <CircularProgress size={16} /> : <LockResetIcon />}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      <BulkInviteDialog
        open={bulkInviteOpen}
        onClose={() => setBulkInviteOpen(false)}
        apiCatalog={apiCatalog}
      />

      </>)}

      {/* Edit permissions dialog */}
      <Dialog open={permissionsDialogOpen} onClose={() => setPermissionsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit permissions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an API from the catalogue, choose HTTP methods, and set optional quotas. Leave quotas empty for 0 usage.
          </Typography>
          <Stack spacing={3}>
            {permissionsEdit.map((r, i) => (
              <Box key={i} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Stack spacing={2}>
                  <Autocomplete
                    size="small"
                    options={(apiCatalog?.apis ?? []).filter((api) => {
                      const selectedElsewhere = permissionsEdit.some(
                        (other, j) => j !== i && other.prefix.replace(/\/\*$/, '') === api.endpoint,
                      );
                      return !selectedElsewhere;
                    })}
                    getOptionLabel={(option) => option.name}
                    value={apiCatalog?.apis.find(api => api.endpoint === r.prefix.replace(/\/\*$/, '')) ?? null}
                    onChange={(_, newValue) => {
                      const next = [...permissionsEdit];
                      next[i] = { ...next[i], prefix: newValue?.endpoint ? `${newValue.endpoint}/*` : '' };
                      setPermissionsEdit(next);
                    }}
                    renderInput={(params) => <TextField {...params} label="API" placeholder="Select an API" />}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.endpoint}/*</Typography>
                        </Box>
                      </li>
                    )}
                  />

                  <Box display="flex" gap={1} alignItems="center">
                    <TextField
                      size="small"
                      label="Quotas"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      value={r.quotas != null ? r.quotas : ''}
                      onChange={(e) => {
                        const next = [...permissionsEdit];
                        const raw = e.target.value.trim();
                        const num = raw === '' ? undefined : parseInt(raw, 10);
                        next[i] = {
                          ...next[i],
                          quotas: raw === '' ? undefined : (Number.isNaN(num) ? undefined : Math.max(0, num as number)),
                        };
                        setPermissionsEdit(next);
                      }}
                      placeholder="No access"
                      sx={{ width: 150 }}
                    />
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setPermissionsEdit(permissionsEdit.filter((_, j) => j !== i))}
                    >
                      Remove
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setPermissionsEdit([...permissionsEdit, { prefix: '', methods: [], quotas: 1000 }])}
            >
              Add rule
            </Button>
          </Stack>
          {updatePermissions.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(updatePermissions.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionsDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={savePermissions}
            disabled={
              updatePermissions.isPending ||
              permissionsEdit.some((r) => !r.prefix.trim())
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

function ApisContent() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedApi, setSelectedApi] = useState<ApiRegistration | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formEndpoint, setFormEndpoint] = useState('');
  const [formNotebookUrl, setFormNotebookUrl] = useState('');
  const [apiSearch, setApiSearch] = useState('');
  const [apiSortBy, setApiSortBy] = useState<'name' | 'description' | 'endpoint' | 'notebookUrl'>('name');
  const [apiSortDir, setApiSortDir] = useState<'asc' | 'desc'>('asc');

  const { data, isLoading, error } = useAdminApis();
  const createApi = useAdminCreateApi();
  const updateApi = useAdminUpdateApi(selectedApi?.apiId || '');
  const deleteApi = useAdminDeleteApi();

  const openCreateDialog = () => {
    setFormName('');
    setFormDescription('');
    setFormEndpoint('');
    setFormNotebookUrl('');
    setSelectedApi(null);
    setCreateDialogOpen(true);
  };

  const openEditDialog = (api: ApiRegistration) => {
    setFormName(api.name);
    setFormDescription(api.description);
    setFormEndpoint(api.endpoint);
    setFormNotebookUrl(api.notebookUrl);
    setSelectedApi(api);
    setEditDialogOpen(true);
  };

  const closeDialogs = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setSelectedApi(null);
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formName,
        description: formDescription,
        endpoint: formEndpoint,
        notebookUrl: formNotebookUrl,
      };

      if (selectedApi) {
        await updateApi.mutateAsync(payload);
      } else {
        await createApi.mutateAsync(payload);
      }
      closeDialogs();
    } catch {
      // Error shown via mutation.error
    }
  };

  const openDeleteConfirm = (api: ApiRegistration) => {
    setSelectedApi(api);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedApi) return;
    try {
      await deleteApi.mutateAsync(selectedApi.apiId);
      setDeleteConfirmOpen(false);
      setSelectedApi(null);
    } catch {
      // Error shown via deleteApi.error
    }
  };

  const isFormValid = formName.trim() && formDescription.trim() && formEndpoint.trim();

  const handleApiSort = (column: typeof apiSortBy) => {
    if (apiSortBy === column) {
      setApiSortDir(apiSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setApiSortBy(column);
      setApiSortDir('asc');
    }
  };

  const filteredSortedApis = (() => {
    if (!data) return [];
    const q = apiSearch.trim().toLowerCase();
    const filtered = q
      ? data.apis.filter((a) =>
          a.name.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q) ||
          a.endpoint.toLowerCase().includes(q) ||
          (a.notebookUrl?.toLowerCase().includes(q) ?? false)
        )
      : data.apis;
    const dir = apiSortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = (a[apiSortBy] ?? '').toString();
      const bv = (b[apiSortBy] ?? '').toString();
      return av.localeCompare(bv) * dir;
    });
  })();

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">API Registry</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search name, endpoint..."
              value={apiSearch}
              onChange={(e) => setApiSearch(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={openCreateDialog}
            >
              Register API
            </Button>
          </Stack>
        </Box>

        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">Failed to load APIs.</Alert>
        )}

        {data && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={apiSortBy === 'name' ? apiSortDir : false}>
                    <TableSortLabel
                      active={apiSortBy === 'name'}
                      direction={apiSortBy === 'name' ? apiSortDir : 'asc'}
                      onClick={() => handleApiSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={apiSortBy === 'description' ? apiSortDir : false}>
                    <TableSortLabel
                      active={apiSortBy === 'description'}
                      direction={apiSortBy === 'description' ? apiSortDir : 'asc'}
                      onClick={() => handleApiSort('description')}
                    >
                      Description
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={apiSortBy === 'endpoint' ? apiSortDir : false}>
                    <TableSortLabel
                      active={apiSortBy === 'endpoint'}
                      direction={apiSortBy === 'endpoint' ? apiSortDir : 'asc'}
                      onClick={() => handleApiSort('endpoint')}
                    >
                      Endpoint
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={apiSortBy === 'notebookUrl' ? apiSortDir : false}>
                    <TableSortLabel
                      active={apiSortBy === 'notebookUrl'}
                      direction={apiSortBy === 'notebookUrl' ? apiSortDir : 'asc'}
                      onClick={() => handleApiSort('notebookUrl')}
                    >
                      Notebook URL
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSortedApis.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>
                        {data.apis.length === 0 ? 'No APIs registered yet.' : 'No APIs match your search.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSortedApis.map((api) => (
                    <TableRow key={api.apiId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {api.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {api.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {api.endpoint}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {api.notebookUrl ? (
                          <Typography
                            variant="body2"
                            component="a"
                            href={api.notebookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            View Docs
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEditDialog(api)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => openDeleteConfirm(api)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={createDialogOpen || editDialogOpen} onClose={closeDialogs} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedApi ? 'Edit API' : 'Register API'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                fullWidth
                required
                placeholder="e.g., Alignment API"
              />
              <TextField
                label="Description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                fullWidth
                required
                multiline
                rows={2}
                placeholder="Brief explanation of the API's purpose"
              />
              <TextField
                label="Endpoint"
                value={formEndpoint}
                onChange={(e) => setFormEndpoint(e.target.value)}
                fullWidth
                required
                placeholder="/v1/alignment"
              />
              <TextField
                label="Notebook URL (optional)"
                value={formNotebookUrl}
                onChange={(e) => setFormNotebookUrl(e.target.value)}
                fullWidth
                placeholder="https://example.com/docs"
              />
            </Stack>
            {(createApi.isError || updateApi.isError) && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {String(createApi.error || updateApi.error)}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialogs}>Cancel</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!isFormValid || createApi.isPending || updateApi.isPending}
              startIcon={(createApi.isPending || updateApi.isPending) ? <CircularProgress size={16} /> : undefined}
            >
              {selectedApi ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => !deleteApi.isPending && setDeleteConfirmOpen(false)}>
          <DialogTitle>Delete API?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>{selectedApi?.name}</strong>?
            </Typography>
            {deleteApi.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {String(deleteApi.error)}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteApi.isPending}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={deleteApi.isPending}
              startIcon={deleteApi.isPending ? <CircularProgress size={16} /> : undefined}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

type BulkRowStatus =
  | 'pending'
  | 'inviting'
  | 'setting-permissions'
  | 'done'
  | 'invite-failed'
  | 'permissions-failed';

interface BulkRow {
  email: string;
  status: BulkRowStatus;
  userId?: string;
  error?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function extractErrorMessage(err: unknown): string {
  const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
  return anyErr?.response?.data?.message || anyErr?.message || 'unknown error';
}

function BulkInviteDialog({
  open,
  onClose,
  apiCatalog,
}: {
  open: boolean;
  onClose: () => void;
  apiCatalog: AdminListApisResponse | undefined;
}) {
  const queryClient = useQueryClient();
  const invite = useAdminInvite();
  const updatePermissions = useAdminUpdatePermissionsMutation();

  const [emailsText, setEmailsText] = useState('');
  const [rules, setRules] = useState<AllowedRule[]>([]);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<BulkRow[]>([]);

  const parsedEmails = (() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const line of emailsText.split('\n')) {
      const e = line.trim();
      if (!e) continue;
      const key = e.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(e);
    }
    return out;
  })();

  const rulesValid = rules.every((r) => r.prefix.trim());
  const canStart = !running && parsedEmails.length > 0 && rulesValid;

  const handleClose = () => {
    if (running) return;
    // If there are any results, refresh users list on close
    if (results.length > 0) {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    }
    setEmailsText('');
    setRules([]);
    setResults([]);
    onClose();
  };

  const updateRow = (index: number, patch: Partial<BulkRow>) => {
    setResults((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleStart = async () => {
    const initial: BulkRow[] = parsedEmails.map((email) => ({
      email,
      status: EMAIL_RE.test(email) ? 'pending' : 'invite-failed',
      error: EMAIL_RE.test(email) ? undefined : 'Invalid email format',
    }));
    setResults(initial);
    setRunning(true);

    for (let i = 0; i < initial.length; i++) {
      if (initial[i].status === 'invite-failed') continue;

      updateRow(i, { status: 'inviting' });
      let userId: string | undefined;
      try {
        const res = await invite.mutateAsync(initial[i].email);
        userId = (res as { userId?: string })?.userId;
      } catch (err) {
        updateRow(i, { status: 'invite-failed', error: extractErrorMessage(err) });
        continue;
      }

      if (rules.length === 0 || !userId) {
        updateRow(i, { status: 'done', userId });
        continue;
      }

      updateRow(i, { status: 'setting-permissions', userId });
      try {
        await updatePermissions.mutateAsync({ userId, allowed: rules });
        updateRow(i, { status: 'done' });
      } catch (err) {
        updateRow(i, { status: 'permissions-failed', error: extractErrorMessage(err) });
      }
    }

    setRunning(false);
  };

  const renderStatusChip = (status: BulkRowStatus) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" size="small" />;
      case 'inviting':
        return (
          <Chip
            label="Inviting"
            size="small"
            color="info"
            icon={<CircularProgress size={12} sx={{ color: 'inherit', ml: 1 }} />}
          />
        );
      case 'setting-permissions':
        return (
          <Chip
            label="Setting permissions"
            size="small"
            color="info"
            icon={<CircularProgress size={12} sx={{ color: 'inherit', ml: 1 }} />}
          />
        );
      case 'done':
        return <Chip label="Done" size="small" color="success" />;
      case 'invite-failed':
        return <Chip label="Invite failed" size="small" color="error" />;
      case 'permissions-failed':
        return <Chip label="Permissions failed" size="small" color="error" />;
    }
  };

  const renderInviteCell = (row: BulkRow) => {
    if (row.status === 'pending' || row.status === 'inviting') {
      return renderStatusChip(row.status);
    }
    if (row.status === 'invite-failed') {
      return <Chip label="Failed" size="small" color="error" />;
    }
    return <Chip label="OK" size="small" color="success" />;
  };

  const renderPermissionsCell = (row: BulkRow) => {
    if (row.status === 'invite-failed') {
      return <Typography variant="caption" color="text.secondary">—</Typography>;
    }
    if (rules.length === 0) {
      return <Typography variant="caption" color="text.secondary">Skipped</Typography>;
    }
    if (row.status === 'pending' || row.status === 'inviting') {
      return <Chip label="Pending" size="small" />;
    }
    if (row.status === 'setting-permissions') {
      return renderStatusChip(row.status);
    }
    if (row.status === 'permissions-failed') {
      return <Chip label="Failed" size="small" color="error" />;
    }
    return <Chip label="OK" size="small" color="success" />;
  };

  const successCount = results.filter((r) => r.status === 'done').length;
  const failCount = results.filter(
    (r) => r.status === 'invite-failed' || r.status === 'permissions-failed',
  ).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk invite users</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste one email per line. The permissions below will be applied to every invited user.
          Leave the permissions empty to invite users without setting any permissions.
        </Typography>

        <TextField
          label="Emails (one per line)"
          value={emailsText}
          onChange={(e) => setEmailsText(e.target.value)}
          multiline
          minRows={4}
          maxRows={12}
          fullWidth
          placeholder={'alice@example.com\nbob@example.com'}
          disabled={running}
          sx={{ mb: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          {parsedEmails.length} unique email{parsedEmails.length === 1 ? '' : 's'} parsed
        </Typography>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Permissions (applied to every user)
        </Typography>
        <Stack spacing={2}>
          {rules.map((r, i) => (
            <Box key={i} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Stack spacing={2}>
                <Autocomplete
                  size="small"
                  disabled={running}
                  options={(apiCatalog?.apis ?? []).filter((api) => {
                    const selectedElsewhere = rules.some(
                      (other, j) => j !== i && other.prefix.replace(/\/\*$/, '') === api.endpoint,
                    );
                    return !selectedElsewhere;
                  })}
                  getOptionLabel={(option) => option.name}
                  value={apiCatalog?.apis.find((api) => api.endpoint === r.prefix.replace(/\/\*$/, '')) ?? null}
                  onChange={(_, newValue) => {
                    const next = [...rules];
                    next[i] = { ...next[i], prefix: newValue?.endpoint ? `${newValue.endpoint}/*` : '' };
                    setRules(next);
                  }}
                  renderInput={(params) => <TextField {...params} label="API" placeholder="Select an API" />}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.endpoint}/*</Typography>
                      </Box>
                    </li>
                  )}
                />

                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    size="small"
                    label="Quotas"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    value={r.quotas != null ? r.quotas : ''}
                    disabled={running}
                    onChange={(e) => {
                      const next = [...rules];
                      const raw = e.target.value.trim();
                      const num = raw === '' ? undefined : parseInt(raw, 10);
                      next[i] = {
                        ...next[i],
                        quotas: raw === '' ? undefined : (Number.isNaN(num) ? undefined : Math.max(0, num as number)),
                      };
                      setRules(next);
                    }}
                    placeholder="No access"
                    sx={{ width: 150 }}
                  />
                  <Button
                    size="small"
                    color="error"
                    disabled={running}
                    onClick={() => setRules(rules.filter((_, j) => j !== i))}
                  >
                    Remove
                  </Button>
                </Box>
              </Stack>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={running}
            onClick={() => setRules([...rules, { prefix: '', methods: [], quotas: 1000 }])}
          >
            Add rule
          </Button>
        </Stack>

        {results.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">Results</Typography>
              <Typography variant="caption" color="text.secondary">
                {successCount} succeeded, {failCount} failed, {results.length} total
              </Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Invite</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">{row.email}</Typography>
                      </TableCell>
                      <TableCell>{renderInviteCell(row)}</TableCell>
                      <TableCell>{renderPermissionsCell(row)}</TableCell>
                      <TableCell>
                        {row.error ? (
                          <Typography variant="caption" color="error">{row.error}</Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={running}>Close</Button>
        <Button
          variant="contained"
          onClick={handleStart}
          disabled={!canStart}
          startIcon={running ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {running ? 'Running...' : `Start (${parsedEmails.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
